import { Box, Flex, Stack, Text } from '@chakra-ui/react';
import { createElement } from 'react';

type ListEmptyProps = {
  icon: React.ComponentType;
  title: string;
  description?: string;
  /** Кнопки под текстом. Ставит их только холодный старт — см. ниже, почему не всегда. */
  children?: React.ReactNode;
};

/**
 * Пустой список внутри экрана — не путать с пустой страницей.
 *
 * Пустых состояний в продукте два, и это две разные роли, а не два вкуса:
 *
 * - `Placeholder` — пустая **страница**: лента без заявок, избранное, поиск без результатов.
 *   Кроме него на экране нет ничего, поэтому он крупный и стоит по центру свободного места.
 * - `ListEmpty` — пустой **список внутри экрана**, где рядом есть другое содержимое: вкладка
 *   друзей над блоком рекомендаций, колонка чатов рядом с открытой перепиской. Он мельче и
 *   компактнее: занять весь экран ему нечем и незачем.
 *
 * Кнопка здесь необязательна и по умолчанию не нужна: раз рядом есть содержимое, действие уже
 * на экране, и вторая кнопка вела бы туда же. Её ставит только холодный старт, когда рядом
 * действительно пусто.
 *
 * Жил этот компонент в папке «Друзья», пока чат не завёл рядом побайтовую копию — те же
 * отступы, та же ширина, те же кегли, только своим файлом. Второй экран и есть повод переселить
 * общее в общее место.
 */
export const ListEmpty = ({ icon, title, description, children }: ListEmptyProps) => (
  <Flex px={4} py={10} justifyContent="center">
    {/* С кнопками текст шире: на 260px «Соберите сессию — остальное подтянется» ломается
        на три строки и расходится с кнопками под ним. */}
    <Stack
      gap={children ? 4 : 2}
      maxW={children ? 'sm' : '260px'}
      w="full"
      textAlign="center"
      alignItems="center"
    >
      {/* fg.icon, а не fg.muted: ступени fg — полупрозрачные, и на пересечениях штрихов
          иконки альфа складывается в пятна. Почему так — в самом токене.

          Иконка ОБОРАЧИВАЕТСЯ, а не подставляется через `as`: `as` подменяет сам элемент,
          и коробка с размером до него не доезжает — иконка растягивалась на всю колонку.
          Так размер и цвет наконец назначает это место, а не сама иконка. */}
      <Box color="fg.icon" boxSize="8" css={{ '& > svg': { width: '100%', height: '100%' } }}>
        {createElement(icon)}
      </Box>
      <Text color="fg.muted" textStyle="title">
        {title}
      </Text>
      {description ? (
        <Text color="fg.muted" textStyle="caption">
          {description}
        </Text>
      ) : null}
      {children}
    </Stack>
  </Flex>
);
